/**
 * The reducer: the state is the fold of the event log. `apply` is pure, validates, and throws an
 * EngineError for anything the rules refuse, so a server can reject a command before it is
 * ever appended. Nothing here reads a clock or a random source; `at` comes with each event.
 */

import type {
  Config,
  Curse,
  EventType,
  GameRecord,
  Lineup,
  LoggedEvent,
  TournamentEvent,
  Player,
  PlayerId,
  Round,
  Score,
  TournamentState,
} from "./types";
import { EngineError } from "./types";
import { eventById, rulesFor, scoreProblem } from "./scoring";
import { currentRound, heldForScores, playersIn, presentIn, roundByNo } from "./selectors";
import { curseProblem, heckleText, isCurseKind, namingRights, NO_SUCH_CURSE, pickLineup, pickProblem, soloStepsOff } from "./bench";
import { mainBoard } from "./ranking";
import { planWindDown, restamp } from "./winddown";

export const MINUTE = 60_000;

/**
 * The mini games (decision 33). Always the whole hall. A scoring one changes the whole game; a
 * handicap is a twist: a minute and a half in the middle of a round, then back to normal. None
 * of them carries fun points, they are for the laugh. Double points went: with no fun points it
 * changed nothing.
 */
export const DEFAULT_EVENT_TYPES: EventType[] = [
  { id: "golden", name: "Golden point at 10-10", kind: "scoring", winBy2: false, mainMultiplier: 1,
    funMultiplier: 0, winBonus: 0, countsOnMain: false },
  { id: "first7", name: "Quickfire: first to 7", kind: "scoring", target: 7, mainMultiplier: 1,
    funMultiplier: 0, winBonus: 0, countsOnMain: false },
  { id: "offhand", name: "Wrong hand only", kind: "handicap", mainMultiplier: 1, funMultiplier: 0,
    winBonus: 0, countsOnMain: false },
  { id: "nosmash", name: "No smashes", kind: "handicap", mainMultiplier: 1, funMultiplier: 0,
    winBonus: 0, countsOnMain: false },
  { id: "underarm", name: "Underarm shots only", kind: "handicap", mainMultiplier: 1, funMultiplier: 0,
    winBonus: 0, countsOnMain: false },
];

export const DEFAULT_CONFIG: Config = {
  courts: 4,
  playersPerCourt: 4,
  gameMs: 13 * MINUTE,
  changeoverMs: 2 * MINUTE,
  target: 11,
  winBy2: true,
  minGamesForPodium: 5,
  catchUpMax: 2,
  // his call, 17 Oct 2026: he watches round 1 to be sure the tech works, then plays
  sitsOutFirst: { name: "Abhi", rounds: 1 },
  // decision 41: an admin starts every round
  autoAdvance: false,
  lastWhistleAt: null,
  randomEvents: false,
  randomEventChance: 0.25,
  twistMs: 90_000,
  eventTypes: DEFAULT_EVENT_TYPES,
  look: { palette: "white", accent: "pink" },
  bench: {
    startingFun: 0,
    heckleEveryMs: 5 * MINUTE,
    heckleMaxChars: 60,
    cursesEach: 1,
    curseMs: MINUTE,
    bounties: [5, 3, 2],
    bountyFromRound: 4,
    pickStreakFrom: 3,
    dubMargin: 8,
    dubRounds: 2,
    nicknameMax: 14,
  },
};

/** The longest message an admin can put on the wall, and how long it stays up. */
export const NOTICE_MAX = 80;
export const NOTICE_MS = 20_000;

/** How many slides the talk before round 1 has (the wall holds what is on them). */
export const SLIDE_COUNT = 7;

/** The longest label a pause can carry ("Lunch"). */
export const PAUSE_LABEL_MAX = 24;

/** Why a mini game is refused while an admin has them switched off. */
export const MINI_GAMES_OFF = "mini games are off for now";

/** How many heckles the state keeps for the wall. */
const HECKLES_KEPT = 20;

const PALETTES = ["white", "cream", "yellow", "cards-on-dark"];
const ACCENTS = ["pink", "cyan", "ink"];
const LETTERINGS = ["grotesque", "round", "condensed", "caps"];

export function emptyState(): TournamentState {
  return {
    config: structuredClone(DEFAULT_CONFIG),
    mode: "americano",
    players: {},
    rounds: [],
    draft: null,
    clock: { phase: "idle", roundNo: null, phaseEndsAt: null, pausedAt: null },
    pausedAll: false,
    pausedUntil: null,
    pauseLabel: null,
    reveal: { main: false, fun: false, awards: false },
    revealStep: null,
    notice: null,
    bench: { muted: false, heckles: [], curses: [], picks: [] },
    windDown: null,
    slides: null,
    seed: 1,
    seq: 0,
  };
}

/**
 * Whether rounds start by themselves in a tournament made by `created`. Its settings say, if they
 * name it. A tournament made before decision 41 never named it and started rounds by itself; it
 * keeps doing so until the live rules tool (on his word) or the switch on the Rounds tab turns
 * it off, so a deploy alone never changes how a day already under way runs.
 */
const AUTO_ADVANCE_BEFORE_41 = true;

export function replay(events: LoggedEvent[], from: TournamentState = emptyState()): TournamentState {
  return events.reduce(apply, from);
}

function player(state: TournamentState, id: PlayerId): Player {
  const p = state.players[id];
  if (!p) throw new EngineError(`no player ${id}`);
  return p;
}

function cleanName(name: string): string {
  const clean = name.trim().replace(/\s+/g, " ");
  if (!clean) throw new EngineError("a name cannot be empty");
  if (clean.length > 24) throw new EngineError("a name has to fit on a court card: 24 letters at most");
  return clean;
}

function newPlayer(state: TournamentState, id: PlayerId, name: string): Player {
  return {
    id,
    name,
    status: "expected",
    catchUpCredits: 0,
    nickname: null,
    photo: 0,
    photoWaived: false,
    dub: null,
    cursesLeft: state.config.bench.cursesEach,
    lastHeckleAt: null,
    heckles: 0,
  };
}

/** A nickname or a typed heckle: trimmed, one line, no longer than it may be. Null when empty. */
function cleanLine(text: string, max: number, what: string): string | null {
  const clean = text.replace(/[\u0000-\u001f\u007f]/g, " ").trim().replace(/\s+/g, " ");
  if (!clean) return null;
  if ([...clean].length > max) throw new EngineError(`${what} can be ${max} characters at most`);
  return clean;
}

/** A pause's label as it will be kept, or null for none. */
function pauseLabel(label: string | null | undefined): string | null {
  if (label === null || label === undefined) return null;
  if (typeof label !== "string") throw new EngineError("a label for the break is a few words");
  return cleanLine(label, PAUSE_LABEL_MAX, "a label for the break");
}

/** When people are due back from a pause, or null for no time given. */
function pauseUntil(until: number | null | undefined): number | null {
  if (until === null || until === undefined) return null;
  if (!Number.isFinite(until)) throw new EngineError("the time to be back is a time");
  return until;
}

function playingNow(state: TournamentState, id: PlayerId): boolean {
  if (state.clock.phase !== "playing") return false;
  const round = roundByNo(state, state.clock.roundNo ?? 0);
  return Boolean(round && playersIn(round.lineup).includes(id));
}

function assertNameFree(state: TournamentState, name: string, except?: PlayerId): void {
  const taken = Object.values(state.players).find(
    (p) => p.id !== except && p.name.toLowerCase() === name.toLowerCase(),
  );
  if (taken) throw new EngineError(`there is already a ${taken.name}`);
}

/** Catch-up credits for someone present for the first time after rounds have been played. */
function checkIn(state: TournamentState, p: Player): void {
  const everPresent = state.rounds.some((r) => presentIn(r.lineup).includes(p.id));
  if (!everPresent && state.rounds.length >= 2) {
    p.catchUpCredits = Math.min(state.config.catchUpMax, Math.floor(state.rounds.length / 2));
  }
}

function validateLineup(state: TournamentState, lineup: Lineup): void {
  const seen = new Set<PlayerId>();
  const courtsSeen = new Set<number>();
  for (const court of lineup.courts) {
    if (!Number.isInteger(court.court) || court.court < 1 || court.court > state.config.courts) {
      throw new EngineError(`there is no court ${court.court}`);
    }
    if (courtsSeen.has(court.court)) throw new EngineError(`court ${court.court} is listed twice`);
    courtsSeen.add(court.court);
    for (const id of [...court.a, ...court.b]) {
      if (seen.has(id)) throw new EngineError(`${player(state, id).name} is on two courts`);
      seen.add(id);
      if (player(state, id).status !== "in") {
        throw new EngineError(`${player(state, id).name} is not available to play`);
      }
    }
    if (court.eventId && !eventById(state.config, court.eventId)) {
      throw new EngineError(`no event ${court.eventId}`);
    }
  }
  for (const id of lineup.sittingOut) {
    if (seen.has(id)) throw new EngineError(`${player(state, id).name} is both playing and sitting out`);
    seen.add(id);
    if (player(state, id).status !== "in") {
      throw new EngineError(`${player(state, id).name} is not here, so cannot be sitting out`);
    }
  }
  for (const id of lineup.catchUp) {
    if (!playersIn(lineup).includes(id)) throw new EngineError(`${player(state, id).name} is not playing`);
    if (player(state, id).catchUpCredits < 1) {
      throw new EngineError(`${player(state, id).name} has no catch-up game left`);
    }
  }
}

function gameAt(state: TournamentState, roundNo: number, court: number): GameRecord {
  const round = roundByNo(state, roundNo);
  if (!round) throw new EngineError(`there is no round ${roundNo}`);
  const game = round.games.find((g) => g.court === court);
  if (!game) throw new EngineError(`round ${roundNo} had nothing on court ${court}`);
  return game;
}

/**
 * Called before a score or a void goes onto a game: if it is the one that lets the empty-hall
 * hold go (the latest round, over, between rounds, nothing in yet), the round remembers when.
 */
function stampRelease(state: TournamentState, roundNo: number, at: number): void {
  const round = currentRound(state);
  if (!round || round.no !== roundNo || state.clock.phase !== "idle") return;
  if (heldForScores(state)) round.releasedAt = at;
}

function sameScore(x: Score, y: Score): boolean {
  return x.a === y.a && x.b === y.b && x.capped === y.capped;
}

function checkScore(state: TournamentState, game: GameRecord, score: Score): void {
  const problem = scoreProblem(score, rulesFor(state.config, eventById(state.config, game.eventId)));
  if (problem) throw new EngineError(problem);
}

/** Why a score queued for a round that was taken back and started again is refused. */
export const TAKEN_BACK = "that round was taken back and started again. This score was for the old draw";

/** Why a settings change with a value the day cannot use is refused. */
export const NOT_A_SETTING = "that setting is not a number the day can use";

/** The longest game a settings change may set. */
const GAME_MS_MAX = 120 * MINUTE;

/**
 * What is wrong with a settings change, checked at the door: a number that is text, out of range
 * or not a number at all would reach the clock (a string game length made the clock read years).
 * Only the keys sent are checked; the look and the mini games are left to the reducer.
 */
function configProblem(patch: unknown): string | null {
  if (typeof patch !== "object" || patch === null || Array.isArray(patch)) return NOT_A_SETTING;
  const p = patch as Record<string, unknown>;
  const has = (key: string): boolean => Object.prototype.hasOwnProperty.call(p, key);
  const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
  const whole = (v: unknown, min: number, max = Infinity): boolean => num(v) && Number.isInteger(v) && v >= min && v <= max;
  if (has("gameMs") && !(num(p.gameMs) && p.gameMs > 0 && p.gameMs <= GAME_MS_MAX)) return NOT_A_SETTING;
  if (has("changeoverMs") && !(num(p.changeoverMs) && p.changeoverMs >= 0)) return NOT_A_SETTING;
  if (has("twistMs") && !(num(p.twistMs) && p.twistMs > 0)) return NOT_A_SETTING;
  if (has("courts") && !whole(p.courts, 1, 4)) return NOT_A_SETTING;
  if (has("target") && !whole(p.target, 1)) return NOT_A_SETTING;
  if (has("minGamesForPodium") && !whole(p.minGamesForPodium, 0)) return NOT_A_SETTING;
  if (has("catchUpMax") && !whole(p.catchUpMax, 0)) return NOT_A_SETTING;
  if (has("lastWhistleAt") && !(p.lastWhistleAt === null || num(p.lastWhistleAt))) return NOT_A_SETTING;
  if (has("sitsOutFirst") && p.sitsOutFirst !== null) {
    const s = p.sitsOutFirst as Record<string, unknown> | undefined;
    if (typeof s !== "object" || !s || typeof s.name !== "string" || !s.name.trim() || !whole(s.rounds, 0, 20)) return NOT_A_SETTING;
  }
  if (has("randomEventChance") && !(num(p.randomEventChance) && p.randomEventChance >= 0 && p.randomEventChance <= 1)) return NOT_A_SETTING;
  for (const key of ["autoAdvance", "randomEvents", "winBy2"]) {
    if (has(key) && typeof p[key] !== "boolean") return "that setting is on or off";
  }
  if (has("bench")) {
    const bench = p.bench;
    if (typeof bench !== "object" || bench === null || Array.isArray(bench)) return NOT_A_SETTING;
    for (const [key, value] of Object.entries(bench)) {
      const ok = key === "bounties"
        ? Array.isArray(value) && value.every((v) => num(v) && v >= 0)
        : num(value) && value >= 0;
      if (!ok) return NOT_A_SETTING;
    }
  }
  return null;
}

/** One line as cleanLine keeps it, for comparing names. */
function asKept(text: string): string {
  return text.replace(/[\u0000-\u001f\u007f]/g, " ").trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Why a nickname or a given name is refused because someone else already goes by it: their real
 * name, their nickname or a name given to them that is still running. Never the player's own.
 */
function nameTaken(state: TournamentState, wanted: string, own: PlayerId): string | null {
  const name = asKept(wanted);
  if (!name) return null;
  const taken = Object.values(state.players).some(
    (p) => p.id !== own && [p.name, p.nickname, p.dub?.nickname].some((n) => typeof n === "string" && asKept(n) === name),
  );
  return taken ? "someone already goes by that name" : null;
}

/**
 * Why a NEW event is refused although `apply` would take it. The reducer has to keep replaying
 * logs written under older rules (a bet, a curse on a court's next game, a mini game on one
 * court), so those shapes are stopped here, at the door, by whoever appends: the server and the
 * pretend day. New checks go here too, never in `apply`, so an old log that broke one still
 * loads: a settings value the day cannot use, a line-up with no game, a name someone else goes
 * by, a score for a round taken back. Null when the event is fine.
 */
export function retired(state: TournamentState, event: TournamentEvent): string | null {
  switch (event.type) {
    case "bet.placed":
      return "bets are picks now: reload the page";
    case "curse.thrown":
      return event.side === undefined ? "curses changed: reload the page" : null;
    case "game.scored": {
      // a score queued for a round that was taken back and started again (same number, new draw)
      if (typeof event.startedAt !== "number") return null;
      const round = roundByNo(state, event.roundNo);
      return round && round.startedAt !== event.startedAt ? TAKEN_BACK : null;
    }
    case "config.updated":
      return configProblem(event.patch);
    case "round.started":
    case "round.drafted":
      return event.lineup && Array.isArray(event.lineup.courts) && event.lineup.courts.length === 0
        ? "nobody is drawn to play: there is no game to start"
        : null;
    case "player.profile":
      return typeof event.nickname === "string" ? nameTaken(state, event.nickname, event.id) : null;
    case "player.dubbed":
      return typeof event.nickname === "string" ? nameTaken(state, event.nickname, event.target) : null;
    case "event.fired": {
      if (state.miniGamesOff) return MINI_GAMES_OFF;
      if (event.courts !== "all") return "a mini game is for the whole hall";
      const type = eventById(state.config, event.eventId);
      const playing = state.clock.phase === "playing" && state.clock.roundNo === event.roundNo;
      if (type?.kind === "handicap" && !playing) return "a twist starts now, in a round being played";
      if (type?.kind === "handicap" && roundByNo(state, event.roundNo)?.twist) return "this round has its twist: stop it first";
      if (type?.kind === "scoring" && playing && twistOrEvent(state)) return "this round already has a mini game";
      return null;
    }
    default:
      return null;
  }
}

/**
 * The event that takes `event` back, worked out from the state BEFORE it was applied, or null
 * when there is no clean way back (a void, a cancelled round, a reset). The admin screen offers
 * it as "Undo" for a few seconds after an action.
 */
export function inverse(before: TournamentState, event: TournamentEvent): TournamentEvent | null {
  switch (event.type) {
    case "player.status": {
      const was = before.players[event.id]?.status;
      return was && was !== event.status ? { type: "player.status", id: event.id, status: was } : null;
    }
    case "player.added":
      return { type: "player.removed", id: event.id };
    case "player.renamed": {
      const was = before.players[event.id]?.name;
      return was ? { type: "player.renamed", id: event.id, name: was } : null;
    }
    case "pause.all": {
      const was = {
        ...(before.pausedUntil !== null ? { until: before.pausedUntil } : {}),
        ...(before.pauseLabel !== null ? { label: before.pauseLabel } : {}),
      };
      if (!event.on) return before.pausedAll ? { type: "pause.all", on: true, ...was } : null;
      if (!before.pausedAll) return { type: "pause.all", on: false };
      // already paused: only the time to be back and the label can have changed
      let changed = event.until !== undefined && event.until !== before.pausedUntil;
      try {
        changed ||= event.label !== undefined && pauseLabel(event.label) !== before.pauseLabel;
      } catch {
        return null; // a label that will be refused
      }
      return changed ? { type: "pause.all", on: true, until: before.pausedUntil, label: before.pauseLabel } : null;
    }
    case "bench.muted":
      return { type: "bench.muted", on: !event.on };
    // Undo flips the switch back and nothing more. Undoing "off" turns curses on again, but the
    // curses that "off" ended stay ended (their throwers keep the curse they were given back).
    case "bench.curses":
      if (typeof event.on !== "boolean" || event.on === !before.bench.cursesOff) return null;
      return { type: "bench.curses", on: !event.on };
    // The same for mini games: undoing "off" turns them on again, but the twist it ended and the
    // mini game it took off the next round do not come back (an admin puts one on again by hand).
    case "minigames.set":
      if (typeof event.on !== "boolean" || event.on === !before.miniGamesOff) return null;
      return { type: "minigames.set", on: !event.on };
    case "reveal":
      return { type: "reveal", board: event.board, on: !event.on };
    case "reveal.step": {
      const was = before.revealStep ?? null;
      return was
        ? { type: "reveal.step", step: was.step, of: was.of, places: was.places }
        : { type: "reveal.step", step: null, of: event.of, places: event.places };
    }
    case "mode.switched":
      return before.mode === event.mode ? null : { type: "mode.switched", mode: before.mode };
    case "config.updated": {
      const patch: Record<string, unknown> = {};
      for (const key of Object.keys(event.patch)) patch[key] = structuredClone((before.config as unknown as Record<string, unknown>)[key]);
      return { type: "config.updated", patch: patch as never };
    }
    case "game.corrected": {
      const was = roundByNo(before, event.roundNo)?.games.find((g) => g.court === event.court)?.score;
      return was ? { type: "game.corrected", roundNo: event.roundNo, court: event.court, score: { ...was }, reason: "undo" } : null;
    }
    case "wall.message":
      return event.text === null ? null : { type: "wall.message", text: null };
    case "event.fired": {
      const kind = eventById(before.config, event.eventId)?.kind;
      if (kind === "handicap") return { type: "twist.stopped" };
      return kind === "scoring" ? { type: "event.cleared", roundNo: event.roundNo } : null;
    }
    case "clock.extended": {
      if (before.clock.phase === "idle" || before.clock.phaseEndsAt === null) return null;
      return Number.isFinite(event.ms) && event.ms !== 0 ? { type: "clock.extended", ms: -event.ms } : null;
    }
    case "lineup.swapped":
      // whoever came off can only go back on if they are still here
      if (before.players[event.out]?.status !== "in") return null;
      return { type: "lineup.swapped", roundNo: event.roundNo, out: event.in, in: event.out };
    case "winddown.set": {
      const was = before.windDown ?? null;
      if (!was) return event.on ? { type: "winddown.set", on: false } : null;
      // on already with the same sum: nothing changed
      if (event.on && was.near === (event.near === true)) return null;
      return { type: "winddown.set", on: true, near: was.near };
    }
    default:
      return null;
  }
}

/**
 * The preview goes, to be drawn again. A whole-game mini game an admin put on it (the same one on
 * every court) is kept, and the next preview carries it (round.drafted). Only the preview and
 * this note change: a round starts from the line-up its own event carries.
 */
function dropDraft(state: TournamentState): void {
  const courts = state.draft?.courts ?? [];
  const id = courts[0]?.eventId;
  if (id && courts.every((c) => c.eventId === id)) state.nextMiniGame = id;
  state.draft = null;
}

function twistOrEvent(state: TournamentState): boolean {
  const round = roundByNo(state, state.clock.roundNo ?? 0);
  return Boolean(round && (round.twist || round.eventIds.length));
}

export function apply(prev: TournamentState, event: LoggedEvent): TournamentState {
  if (event.seq !== prev.seq + 1) {
    throw new EngineError(`event ${event.seq} is out of order after ${prev.seq}`);
  }
  const state = structuredClone(prev);
  state.seq = event.seq;
  const { at } = event;

  switch (event.type) {
    case "tournament.created": {
      state.config = { ...structuredClone(DEFAULT_CONFIG), ...structuredClone(event.config) };
      // a config written before a bench number existed gets today's number for it
      state.config.bench = { ...structuredClone(DEFAULT_CONFIG.bench), ...structuredClone(event.config.bench ?? {}) };
      if (typeof event.config.autoAdvance !== "boolean") state.config.autoAdvance = AUTO_ADVANCE_BEFORE_41;
      state.windDown = null;
      state.slides = null;
      delete state.miniGamesOff;
      delete state.nextMiniGame;
      state.seed = event.seed >>> 0;
      state.players = {};
      state.rounds = [];
      state.draft = null;
      state.bench = { muted: false, heckles: [], curses: [], picks: [] };
      state.mode = "americano";
      for (const { id, name } of event.players) {
        if (state.players[id]) throw new EngineError(`player id ${id} is used twice`);
        const clean = cleanName(name);
        assertNameFree(state, clean);
        state.players[id] = newPlayer(state, id, clean);
      }
      break;
    }
    case "config.updated": {
      const next = { ...state.config, ...event.patch, playersPerCourt: 4 as const };
      if (!Number.isInteger(next.courts) || next.courts < 1) throw new EngineError("courts must be 1 or more");
      if (next.gameMs <= 0 || next.changeoverMs < 0) throw new EngineError("the slot times must be positive");
      if (!Number.isInteger(next.target) || next.target < 1) throw new EngineError("the target must be 1 or more");
      if (next.minGamesForPodium < 0 || next.catchUpMax < 0) throw new EngineError("counts cannot be negative");
      if (next.randomEventChance < 0 || next.randomEventChance > 1) throw new EngineError("chance is 0 to 1");
      if (!(next.twistMs > 0)) throw new EngineError("a twist lasts some time");
      if (event.patch.bench) next.bench = { ...state.config.bench, ...event.patch.bench };
      // a preview drawn for another number of courts is no use
      if (next.courts !== state.config.courts) dropDraft(state);
      if (!next.look || !PALETTES.includes(next.look.palette) || !ACCENTS.includes(next.look.accent)) {
        throw new EngineError("that look is not one of the palettes");
      }
      if (next.look.lettering !== undefined && !LETTERINGS.includes(next.look.lettering)) {
        throw new EngineError("that lettering is not one of the choices");
      }
      state.config = next;
      break;
    }
    case "player.added": {
      if (state.players[event.id]) throw new EngineError(`player id ${event.id} already exists`);
      const clean = cleanName(event.name);
      assertNameFree(state, clean);
      const p = newPlayer(state, event.id, clean);
      state.players[event.id] = p;
      if (event.status && event.status !== "expected") {
        p.status = event.status;
        if (event.status === "in") checkIn(state, p);
      }
      break;
    }
    case "player.renamed": {
      const p = player(state, event.id);
      const clean = cleanName(event.name);
      assertNameFree(state, clean, p.id);
      p.name = clean;
      break;
    }
    case "player.removed": {
      const p = player(state, event.id);
      if (state.rounds.some((r) => presentIn(r.lineup).includes(p.id))) {
        throw new EngineError(`${p.name} has been in a round; mark them as left instead`);
      }
      delete state.players[event.id];
      if (state.draft && presentIn(state.draft).includes(p.id)) dropDraft(state);
      break;
    }
    case "player.status": {
      const p = player(state, event.id);
      if (p.status === event.status) break;
      p.status = event.status;
      if (event.status === "in") checkIn(state, p);
      if (state.draft && (presentIn(state.draft).includes(p.id) || event.status === "in")) {
        // the preview no longer matches who is here; the server draws it again
        dropDraft(state);
      }
      break;
    }
    case "round.drafted": {
      if (event.lineup) validateLineup(state, event.lineup);
      // a preview drawn again (Regenerate) keeps the mini game set on the one it replaces
      dropDraft(state);
      state.draft = event.lineup ? structuredClone(event.lineup) : null;
      const kept = state.nextMiniGame;
      if (
        state.draft && kept && state.draft.courts.length && !state.draft.courts.some((c) => c.eventId) &&
        !state.miniGamesOff && eventById(state.config, kept)?.kind === "scoring"
      ) {
        for (const court of state.draft.courts) court.eventId = kept;
      }
      break;
    }
    case "round.started": {
      if (state.pausedAll) throw new EngineError("everything is paused");
      if (state.clock.phase === "playing") throw new EngineError("a round is still being played");
      validateLineup(state, event.lineup);
      if (event.lineup.mode !== state.mode) throw new EngineError(`that line-up is ${event.lineup.mode}, the mode is ${state.mode}`);
      const eventIds = event.eventIds ?? [];
      // with mini games off a round starts plain (the autopilot rolls none; a screen holding an
      // old preview with one on it is told why and sends the plain one it is shown next)
      if (state.miniGamesOff && (eventIds.length || event.twist || event.lineup.courts.some((c) => c.eventId))) {
        throw new EngineError(MINI_GAMES_OFF);
      }
      for (const id of eventIds) if (!eventById(state.config, id)) throw new EngineError(`no event ${id}`);
      if (event.twist) {
        if (eventById(state.config, event.twist.eventId)?.kind !== "handicap") throw new EngineError("a twist has to be one of the handicaps");
        if (!(event.twist.afterMs >= 0) || event.twist.afterMs + state.config.twistMs > state.config.gameMs) {
          throw new EngineError("a twist has to fit inside the game");
        }
      }
      const no = state.rounds.length + 1;
      const lineup = structuredClone(event.lineup);
      const round: Round = {
        no,
        mode: lineup.mode,
        startedAt: at,
        lineup,
        eventIds: [...eventIds],
        games: lineup.courts.map((c) => ({
          court: c.court,
          a: [...c.a] as [PlayerId, PlayerId],
          b: [...c.b] as [PlayerId, PlayerId],
          eventId: c.eventId ?? eventIds[0] ?? null,
          score: null,
          voided: false,
          voidReason: null,
          history: [],
          cursedBy: null,
          dubUsed: false,
        })),
        bounties: [],
        twist: event.twist
          ? { eventId: event.twist.eventId, startsAt: at + event.twist.afterMs, endsAt: at + event.twist.afterMs + state.config.twistMs }
          : null,
      };
      for (const id of lineup.catchUp) player(state, id).catchUpCredits -= 1;
      // picks made on the preview only stand if that pair is on that court, and the picker is resting
      const onCourtNow = new Set(playersIn(lineup));
      state.bench.picks = state.bench.picks.filter((pick) => {
        if (pick.roundNo !== no) return true;
        const game = round.games.find((g) => g.court === pick.court);
        const there = game && [game.a, game.b].some((pair) => pair.includes(pick.pair[0]) && pair.includes(pick.pair[1]));
        return Boolean(there) && !onCourtNow.has(pick.by);
      });
      // a bounty on the main board's top three, if they are on court
      if (no >= state.config.bench.bountyFromRound) {
        const top = mainBoard(state).filter((r) => r.games > 0).slice(0, state.config.bench.bounties.length);
        const onCourt = new Set(playersIn(lineup));
        top.forEach((r, i) => {
          const value = state.config.bench.bounties[i] ?? 0;
          if (value > 0 && onCourt.has(r.id)) round.bounties.push({ player: r.id, value });
        });
      }
      state.rounds.push(round);
      // a given name runs out
      for (const p of Object.values(state.players)) if (p.dub && no > p.dub.untilRound) p.dub = null;
      state.draft = null;
      delete state.nextMiniGame;
      // a round starting ends the slides
      state.slides = null;
      state.clock = { phase: "playing", roundNo: no, phaseEndsAt: at + state.config.gameMs, pausedAt: null };
      break;
    }
    case "round.finished": {
      if (state.clock.phase !== "playing") throw new EngineError("no round is being played");
      state.clock = {
        phase: "changeover",
        roundNo: state.clock.roundNo,
        phaseEndsAt: at + state.config.changeoverMs,
        pausedAt: state.pausedAll ? at : null,
      };
      break;
    }
    case "round.closed": {
      if (state.clock.phase !== "changeover") throw new EngineError("not in a changeover");
      state.clock = { phase: "idle", roundNo: null, phaseEndsAt: null, pausedAt: state.pausedAll ? at : null };
      break;
    }
    case "clock.extended": {
      if (state.clock.phase === "idle" || state.clock.phaseEndsAt === null) {
        throw new EngineError("there is no clock to extend between rounds");
      }
      if (!Number.isFinite(event.ms)) throw new EngineError("extend by a number of milliseconds");
      // time can come off too (an undo), but never past now: at zero the autopilot ends the phase
      const nowOnClock = state.pausedAll && state.clock.pausedAt !== null ? state.clock.pausedAt : at;
      state.clock.phaseEndsAt = Math.max(state.clock.phaseEndsAt + event.ms, nowOnClock);
      break;
    }
    case "pause.all": {
      if (event.on && state.pausedAll) {
        // paused already: when it began and the frozen clock stay; only what was sent changes
        if (event.until !== undefined) state.pausedUntil = pauseUntil(event.until);
        if (event.label !== undefined) state.pauseLabel = pauseLabel(event.label);
        break;
      }
      if (event.on === state.pausedAll) break;
      if (event.on) {
        state.pausedUntil = pauseUntil(event.until);
        state.pauseLabel = pauseLabel(event.label);
        state.pausedAll = true;
        state.clock.pausedAt = at;
      } else {
        if (state.clock.phaseEndsAt !== null && state.clock.pausedAt !== null) {
          const pausedAt = state.clock.pausedAt;
          const held = at - pausedAt;
          state.clock.phaseEndsAt += held;
          // a twist or a curse that had not finished waits out the pause too
          const round = roundByNo(state, state.clock.roundNo ?? 0);
          const windows = [round?.twist, ...state.bench.curses.filter((c) => c.roundNo === round?.no)];
          for (const w of windows) {
            if (!w || w.endsAt <= pausedAt) continue;
            w.startsAt += held;
            w.endsAt += held;
          }
        }
        state.clock.pausedAt = null;
        state.pausedAll = false;
        state.pausedUntil = null;
        state.pauseLabel = null;
      }
      break;
    }
    case "game.scored": {
      const game = gameAt(state, event.roundNo, event.court);
      if (game.voided) throw new EngineError(`that game was voided: ${game.voidReason ?? "no reason given"}`);
      checkScore(state, game, event.score);
      if (game.score) {
        if (sameScore(game.score, event.score)) break; // a tablet retrying the same entry
        throw new EngineError("that game already has a score; correct it instead");
      }
      stampRelease(state, event.roundNo, at);
      game.history.push({ score: { ...event.score }, by: event.by, at });
      game.score = { ...event.score };
      if (state.mode === "mexicano") dropDraft(state); // the preview follows the board
      break;
    }
    case "game.corrected": {
      const game = gameAt(state, event.roundNo, event.court);
      if (game.voided) throw new EngineError("that game was voided; it cannot be corrected");
      checkScore(state, game, event.score);
      stampRelease(state, event.roundNo, at);
      game.history.push({ score: { ...event.score }, by: event.by, at, reason: event.reason });
      game.score = { ...event.score };
      if (state.mode === "mexicano") dropDraft(state);
      break;
    }
    case "game.voided": {
      const game = gameAt(state, event.roundNo, event.court);
      stampRelease(state, event.roundNo, at);
      game.voided = true;
      game.voidReason = event.reason;
      // during a wind down a void gives four people a game back to play, so the preview is drawn again
      if (state.mode === "mexicano" || state.windDown) dropDraft(state);
      break;
    }
    case "mode.switched": {
      if (state.mode === event.mode) break;
      state.mode = event.mode;
      dropDraft(state);
      break;
    }
    case "event.fired": {
      if (state.miniGamesOff) throw new EngineError(MINI_GAMES_OFF);
      if (!eventById(state.config, event.eventId)) throw new EngineError(`no event ${event.eventId}`);
      const wanted = (court: number): boolean => event.courts === "all" || event.courts.includes(court);
      const round = roundByNo(state, event.roundNo);
      const type = eventById(state.config, event.eventId);
      if (round && type?.kind === "handicap" && event.courts === "all") {
        // a twist, starting now
        if (state.clock.roundNo !== round.no || state.clock.phase !== "playing") throw new EngineError(`round ${round.no} is over`);
        const ends = state.clock.phaseEndsAt ?? at;
        round.twist = { eventId: event.eventId, startsAt: at, endsAt: Math.max(at, Math.min(at + state.config.twistMs, ends)) };
      } else if (round) {
        if (state.clock.roundNo !== round.no) throw new EngineError(`round ${round.no} is over`);
        for (const game of round.games) {
          if (!wanted(game.court)) continue;
          if (game.score) throw new EngineError(`court ${game.court} has already entered a score`);
          game.eventId = event.eventId;
        }
        if (event.courts === "all" && !round.eventIds.includes(event.eventId)) round.eventIds.push(event.eventId);
      } else if (event.roundNo === state.rounds.length + 1 && state.draft) {
        for (const court of state.draft.courts) if (wanted(court.court)) court.eventId = event.eventId;
        // the preview carries it now (and dropDraft keeps it from there)
        delete state.nextMiniGame;
      } else {
        throw new EngineError(`there is no round ${event.roundNo} to fire an event on`);
      }
      break;
    }
    case "event.cleared": {
      const round = roundByNo(state, event.roundNo);
      if (round) {
        if (state.clock.roundNo !== round.no) throw new EngineError(`round ${round.no} is over`);
        if (round.games.some((g) => g.score)) throw new EngineError("a score is in under that mini game");
        for (const game of round.games) game.eventId = null;
        round.eventIds = [];
      } else if (event.roundNo === state.rounds.length + 1 && state.draft) {
        for (const court of state.draft.courts) court.eventId = null;
        delete state.nextMiniGame;
      } else if (event.roundNo === state.rounds.length + 1 && state.nextMiniGame) {
        // set for the next round while its preview is being drawn again
        delete state.nextMiniGame;
      } else {
        throw new EngineError(`there is no round ${event.roundNo}`);
      }
      break;
    }
    case "lineup.swapped": {
      const leaving = player(state, event.out);
      const coming = player(state, event.in);
      if (leaving.id === coming.id) throw new EngineError("swap two different players");
      // a started round can be put right until that court has its score, even after the whistle
      // (when the wrong name is noticed) or with a later round on; the preview any time
      const round = roundByNo(state, event.roundNo);
      const over = Boolean(round) && !(state.clock.phase === "playing" && state.clock.roundNo === event.roundNo);
      const lineup = round ? round.lineup : event.roundNo === state.rounds.length + 1 ? state.draft : null;
      if (!lineup) throw new EngineError(`round ${event.roundNo} cannot be changed`);
      const from = lineup.courts.find((c) => c.a.includes(leaving.id) || c.b.includes(leaving.id));
      if (!from) throw new EngineError(`${leaving.name} is not playing in round ${event.roundNo}`);
      // once the round is over, whoever really played may have gone home since
      if (!over && coming.status !== "in") throw new EngineError(`${coming.name} is not here to play`);
      // but never someone who has not walked in at all
      if (over && coming.status === "expected") throw new EngineError(`${coming.name} has not arrived`);
      const to = lineup.courts.find((c) => c.a.includes(coming.id) || c.b.includes(coming.id));
      if (over && to) throw new EngineError(`round ${event.roundNo} is over: only someone who was not on court in it can come on`);
      const touched = to && to !== from ? [from, to] : [from];
      if (round) {
        for (const court of touched) {
          const game = round.games.find((g) => g.court === court.court);
          if (!game || game.score || game.voided) throw new EngineError(`court ${court.court} already has its score`);
        }
      }
      // both on court: they change places. Otherwise one comes off the bench.
      const HOLD = "\u0000swap";
      const put = (ids: PlayerId[], was: PlayerId, now: PlayerId): void => {
        const i = ids.indexOf(was);
        if (i >= 0) ids[i] = now;
      };
      for (const court of touched) for (const pair of [court.a, court.b]) put(pair, leaving.id, HOLD);
      if (to) for (const pair of [to.a, to.b]) put(pair, coming.id, leaving.id);
      for (const court of touched) for (const pair of [court.a, court.b]) put(pair, HOLD, coming.id);
      if (!to) {
        lineup.sittingOut = lineup.sittingOut.filter((id) => id !== coming.id);
        if (leaving.status === "in") lineup.sittingOut.push(leaving.id);
        if (lineup.catchUp.includes(leaving.id)) {
          lineup.catchUp = lineup.catchUp.filter((id) => id !== leaving.id);
          if (round) leaving.catchUpCredits += 1;
        }
      }
      if (round) {
        for (const court of touched) {
          const game = round.games.find((g) => g.court === court.court)!;
          game.a = [...court.a] as [PlayerId, PlayerId];
          game.b = [...court.b] as [PlayerId, PlayerId];
        }
        // a bounty stays with a player who is still on court
        const onCourt = new Set(playersIn(lineup));
        round.bounties = round.bounties.filter((b) => onCourt.has(b.player));
        // during a wind down the next round's preview was drawn from who had played what: someone
        // coming on may now be on the target, so the autopilot draws it again
        if (state.windDown) dropDraft(state);
        // put on a court after the whistle: a pick they made on their own game does not stand
        if (over) {
          state.bench.picks = state.bench.picks.filter((p) => !(p.by === coming.id && p.roundNo === round.no && p.court === from.court));
        }
      }
      break;
    }
    case "round.cancelled": {
      const round = roundByNo(state, state.clock.roundNo ?? 0);
      if (state.clock.phase !== "playing" || !round || round.no !== state.rounds.length) {
        throw new EngineError("there is no round being played to take back");
      }
      if (round.games.some((g) => g.score)) throw new EngineError("a score is already in: void the games instead");
      for (const id of round.lineup.catchUp) if (state.players[id]) state.players[id].catchUpCredits += 1;
      // curses thrown in it go back to whoever threw them
      for (const curse of state.bench.curses.filter((c) => c.roundNo === round.no)) {
        const thrower = state.players[curse.by];
        if (thrower) thrower.cursesLeft += 1;
      }
      state.bench.curses = state.bench.curses.filter((c) => c.roundNo !== round.no);
      state.rounds.pop();
      // the same line-up is the preview again, if everyone in it is still here and it still fits
      // the mode and the courts in use; otherwise the autopilot draws a new one
      const lineup = structuredClone(round.lineup);
      for (const court of lineup.courts) court.eventId = null;
      const stillHere = presentIn(lineup).every((id) => state.players[id]?.status === "in");
      const fits = lineup.mode === state.mode && lineup.courts.every((c) => c.court <= state.config.courts);
      state.draft = stillHere && fits ? lineup : null;
      delete state.nextMiniGame;
      state.clock = { phase: "idle", roundNo: null, phaseEndsAt: null, pausedAt: state.pausedAll ? at : null };
      // or the autopilot would start it again at once
      state.config.autoAdvance = false;
      break;
    }
    case "wall.message": {
      if (event.text === null) {
        state.notice = null;
        break;
      }
      const text = cleanLine(event.text, NOTICE_MAX, "a message for the wall");
      if (!text) throw new EngineError("a message needs something to say");
      state.notice = { text, at, by: event.by };
      break;
    }
    case "twist.stopped": {
      const round = roundByNo(state, state.clock.roundNo ?? 0);
      if (!round?.twist || round.twist.endsAt <= at) throw new EngineError("there is no twist to stop");
      if (round.twist.startsAt >= at) round.twist = null;
      else round.twist.endsAt = at;
      break;
    }
    case "reveal": {
      state.reveal[event.board] = event.on;
      break;
    }
    case "reveal.step": {
      if (event.step === null) {
        state.revealStep = null;
        state.reveal = { main: false, fun: false, awards: false };
        break;
      }
      const whole = (n: unknown, max: number): n is number => typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= max;
      if (!whole(event.of, 60) || !whole(event.places, 3)) throw new EngineError("the reveal's count of awards and places is wrong: reload the page");
      const last = event.of + Math.max(event.places, 1);
      if (!whole(event.step, last) || event.step < 1) throw new EngineError(`the reveal has ${last} steps: pick one from 1 to ${last}`);
      state.revealStep = { step: event.step, of: event.of, places: event.places, at };
      // the awards' fun points count from the first award, as with the old switch; the podium is on from its first place
      state.reveal.awards = true;
      state.reveal.main = event.step > event.of;
      break;
    }
    case "player.profile": {
      const p = player(state, event.id);
      if (event.nickname !== undefined) {
        p.nickname = event.nickname === null ? null : cleanLine(event.nickname, state.config.bench.nicknameMax, "a nickname");
      }
      if (event.photo !== undefined) {
        if (!Number.isInteger(event.photo) || event.photo < 0) throw new EngineError("a picture version is a whole number");
        p.photo = event.photo;
      }
      if (event.photoWaived !== undefined) p.photoWaived = event.photoWaived;
      break;
    }
    case "heckle.sent": {
      const p = player(state, event.player);
      if (state.bench.muted) throw new EngineError("heckles are off for now");
      if (playingNow(state, p.id)) throw new EngineError("you are on court; heckle when you are resting");
      if (event.court !== null && (!Number.isInteger(event.court) || event.court < 1 || event.court > state.config.courts)) {
        throw new EngineError(`there is no court ${event.court}`);
      }
      const every = state.config.bench.heckleEveryMs;
      const wait = p.lastHeckleAt === null ? 0 : p.lastHeckleAt + every - at;
      if (wait > 0) {
        throw new EngineError(`one heckle every ${Math.round(every / MINUTE)} minutes: ${Math.ceil(wait / MINUTE)} to go`);
      }
      const text = event.text !== undefined
        ? cleanLine(event.text, state.config.bench.heckleMaxChars, "a heckle")
        : heckleText(event.line ?? -1, event.court);
      if (!text) throw new EngineError("a heckle needs something to say");
      p.lastHeckleAt = at;
      p.heckles = (p.heckles ?? 0) + 1;
      state.bench.heckles.push({ seq: event.seq, by: p.id, court: event.court, text, at });
      if (state.bench.heckles.length > HECKLES_KEPT) state.bench.heckles.splice(0, state.bench.heckles.length - HECKLES_KEPT);
      break;
    }
    case "heckle.removed": {
      // a heckle taken off the wall does not count for Loudest heckler (one no longer kept is left alone)
      const heckle = state.bench.heckles.find((h) => h.seq === event.heckleSeq);
      const sender = heckle ? state.players[heckle.by] : undefined;
      if (sender) sender.heckles = Math.max(0, (sender.heckles ?? 1) - 1);
      state.bench.heckles = state.bench.heckles.filter((h) => h.seq !== event.heckleSeq);
      break;
    }
    case "bench.muted": {
      state.bench.muted = event.on;
      break;
    }
    case "bench.curses": {
      if (typeof event.on !== "boolean") throw new EngineError("curses are on or off");
      if (event.on) {
        // on again: whoever has a curse left can throw it, as before
        state.bench.cursesOff = false;
        break;
      }
      if (state.bench.cursesOff) break;
      state.bench.cursesOff = true;
      // a curse running now ends, and its thrower gets it back: it was taken away through no
      // fault of theirs. The round's clock is the one that counts (frozen while paused).
      const t = state.pausedAll && state.clock.pausedAt !== null ? state.clock.pausedAt : at;
      const playing = state.clock.phase === "playing" ? state.clock.roundNo : null;
      const running = state.bench.curses.filter((c) => c.roundNo === playing && c.endsAt > t);
      state.bench.curses = state.bench.curses.filter((c) => !running.includes(c));
      for (const curse of running) {
        const game = roundByNo(state, curse.roundNo)?.games.find((g) => g.court === curse.court);
        if (game) game.cursedBy = null;
        const thrower = state.players[curse.by];
        if (thrower) thrower.cursesLeft += 1;
      }
      break;
    }
    case "minigames.set": {
      if (typeof event.on !== "boolean") throw new EngineError("mini games are on or off");
      if (event.on) {
        state.miniGamesOff = false;
        break;
      }
      if (state.miniGamesOff) break;
      state.miniGamesOff = true;
      // a twist running now, or still to come in this round, ends as Stop the twist ends it (by
      // the round's clock, frozen while paused, so a resume does not bring it back)
      const t = state.pausedAll && state.clock.pausedAt !== null ? state.clock.pausedAt : at;
      const round = state.clock.phase === "playing" ? roundByNo(state, state.clock.roundNo ?? 0) : undefined;
      if (round?.twist && round.twist.endsAt > t) {
        if (round.twist.startsAt >= t) round.twist = null;
        else round.twist.endsAt = t;
      }
      // the mini game put on the next round comes off its preview. A round already being played
      // under a whole-game one (Golden point, Quickfire) keeps its scoring rule: the rules of a
      // game are not changed in the middle of it, and a score may already be in under them.
      if (state.draft) for (const court of state.draft.courts) court.eventId = null;
      delete state.nextMiniGame;
      break;
    }
    case "curse.thrown": {
      // the old shape (a handicap on a court's next game) is in old logs: it does nothing now
      if (event.side === undefined) break;
      const p = player(state, event.player);
      const problem = curseProblem(state, p.id, at);
      if (problem) throw new EngineError(problem);
      if (event.side !== "a" && event.side !== "b") throw new EngineError("curse one pair or the other");
      // missing is wrong hand: every log written before the choice replays as it was played
      if (event.kind !== undefined && !isCurseKind(event.kind)) throw new EngineError(NO_SUCH_CURSE);
      const round = roundByNo(state, state.clock.roundNo ?? 0);
      const game = round?.games.find((g) => g.court === event.court);
      if (!round || !game) throw new EngineError(`nothing is being played on court ${event.court}`);
      if (game.score || game.voided) throw new EngineError("that game is over");
      if (state.bench.curses.some((c) => c.roundNo === round.no && c.court === event.court && c.endsAt > at)) {
        throw new EngineError(`court ${event.court} is already cursed`);
      }
      p.cursesLeft -= 1;
      game.cursedBy = p.id;
      const curse: Curse = { by: p.id, roundNo: round.no, court: event.court, side: event.side, startsAt: at, endsAt: at + state.config.bench.curseMs };
      // only what the event says is kept: an old log's curse stays exactly as it was
      if (event.kind !== undefined) curse.kind = event.kind;
      // Solo: who steps off is drawn here from the seed, never taken from the phone
      if (event.kind === "solo") curse.stepsOff = soloStepsOff(state, round.no, event.court, game[event.side]);
      state.bench.curses.push(curse);
      break;
    }
    case "curse.lifted": {
      const curse = state.bench.curses.find((c) => c.roundNo === state.clock.roundNo && c.court === event.court && c.endsAt > at);
      if (!curse) throw new EngineError(`court ${event.court} is not cursed`);
      state.bench.curses = state.bench.curses.filter((c) => c !== curse);
      const game = roundByNo(state, curse.roundNo)?.games.find((g) => g.court === curse.court);
      if (game) game.cursedBy = null;
      const thrower = state.players[curse.by];
      if (thrower) thrower.cursesLeft += 1;
      break;
    }
    case "pick.made": {
      const p = player(state, event.player);
      const problem = pickProblem(state, event.roundNo, at);
      if (problem) throw new EngineError(problem);
      const lineup = pickLineup(state, event.roundNo);
      const court = lineup?.courts.find((c) => c.court === event.court);
      if (!lineup || !court) throw new EngineError(`nothing is on court ${event.court} that round`);
      if (playersIn(lineup).includes(p.id)) throw new EngineError("you are on court that round; pick when you are resting");
      if (event.side !== "a" && event.side !== "b") throw new EngineError("pick one pair or the other");
      const game = roundByNo(state, event.roundNo)?.games.find((g) => g.court === event.court);
      if (game && (game.score || game.voided)) throw new EngineError("that game is over");
      // one pick a court a round; picking again changes it
      state.bench.picks = state.bench.picks.filter((x) => !(x.by === p.id && x.roundNo === event.roundNo && x.court === event.court));
      state.bench.picks.push({ by: p.id, roundNo: event.roundNo, court: event.court, pair: [...court[event.side]] as [PlayerId, PlayerId] });
      break;
    }
    case "bet.placed": {
      // bets became free picks (decision 33); an old log's bets do nothing
      break;
    }
    case "player.dubbed": {
      const winner = player(state, event.player);
      const target = player(state, event.target);
      const right = namingRights(state, winner.id).find((r) => r.losers.includes(target.id));
      if (!right) throw new EngineError(`win by ${state.config.bench.dubMargin} or more against them to name them`);
      const nickname = cleanLine(event.nickname, state.config.bench.nicknameMax, "a nickname");
      if (!nickname) throw new EngineError("a name cannot be empty");
      target.dub = { nickname, by: winner.id, untilRound: state.rounds.length + state.config.bench.dubRounds };
      gameAt(state, right.roundNo, right.court).dubUsed = true;
      break;
    }
    case "player.undubbed": {
      player(state, event.id).dub = null;
      break;
    }
    case "winddown.set": {
      if (typeof event.on !== "boolean") throw new EngineError("wind down is on or off");
      if (!event.on) {
        state.windDown = null;
      } else {
        const near = event.near === true;
        if (state.windDown && state.windDown.near === near) break;
        // the level sum asked for when it cannot be done (an Undo of Cancel after the sum had
        // moved to the near one, or someone leaving as Start is tapped): the near sum, as a
        // recount would take it (winddown.ts restamp)
        const plan = planWindDown(state, near) ?? (near ? null : planWindDown(state, true));
        if (!plan) throw new EngineError("everyone level cannot be done with this many here");
        state.windDown = {
          at,
          by: event.by,
          near,
          target: plan.target,
          short: plan.short,
          first: { target: plan.target, rounds: plan.rounds.length, short: plan.short },
        };
      }
      // the preview was drawn under the other rules; the autopilot draws it again
      dropDraft(state);
      break;
    }
    case "slides.set": {
      if (event.slide === null) {
        state.slides = null;
        break;
      }
      if (typeof event.slide !== "number" || !Number.isInteger(event.slide) || event.slide < 0 || event.slide >= SLIDE_COUNT) {
        throw new EngineError(`there are ${SLIDE_COUNT} slides: pick one from 1 to ${SLIDE_COUNT}`);
      }
      state.slides = event.slide;
      break;
    }
    default: {
      const never: never = event;
      throw new EngineError(`unknown event ${String((never as { type?: unknown }).type)}`);
    }
  }
  // a wind down keeps its sum while it adds up, and is worked out again when it does not
  if (state.windDown) state.windDown = restamp(state);
  return state;
}
